import { placeholderTokenPattern } from '@pbk-web/utils';
import type { LexicalEditor } from 'lexical';
import { TextNode } from 'lexical';

import { CONTENT_LOAD_TAG } from '../../core/updateTags';
import { $createDynamicDataNode } from './DynamicDataNode';

/**
 * Turns `${{key}}` tokens in text (a loaded value, a paste) into `DynamicDataNode` chips as they appear.
 * - Keys follow the shared placeholder grammar (`@pbk-web/utils`), the same rule forms-v2 uses.
 */
export const registerDynamicDataTransform = (editor: LexicalEditor): (() => void) => {
  const transform = (node: TextNode) => {
    // Skips token/segmented text, which can't be split
    if (!node.isSimpleText()) return;
    const text = node.getTextContent();
    const match = placeholderTokenPattern().exec(text);
    if (!match) return;

    const start = match.index;
    const end = start + match[0].length;
    const parts = node.splitText(...[start, end].filter((offset) => offset > 0 && offset < text.length));
    // The text after the match is a new node, so this transform runs again on it
    parts[start === 0 ? 0 : 1].replace($createDynamicDataNode(match[1]));
  };

  let unregister: () => void = () => undefined;
  // Registering marks every text node dirty; load-tagged, so chips it brings back aren't reported as added
  editor.update(
    () => {
      unregister = editor.registerNodeTransform(TextNode, transform);
    },
    { tag: CONTENT_LOAD_TAG },
  );
  return () => unregister();
};
